import { MouseEvent, useState } from 'react'
import { Link } from 'react-router-dom'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ChainLink, demandRequestPath, fulfillmentOrderPath, transferOrderPath } from '../chain'

const SEPARATOR = <Box component="span" aria-hidden sx={{ color: 'text.disabled', px: 0.25 }}>›</Box>

/** `REQ-1234 › FO-165 › TO-88`: where one fulfillment order came from and what it became. */
export function ChainRow( { link }: { link: ChainLink } ) {
	return (
		<Stack
			direction="row"
			alignItems="center"
			useFlexGap
			flexWrap="wrap"
			sx={{ columnGap: 0.75, rowGap: 0.25, typography: 'body2', whiteSpace: 'nowrap' }}
		>
			{link.requests.map( ( request ) => (
				<Box component="span" key={request.id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
					<Link to={demandRequestPath( request.id )}>REQ-{request.requestNumber}</Link>
				</Box>
			) )}
			<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
				{link.requests.length > 0 && SEPARATOR}
				<Link to={fulfillmentOrderPath( link.orderNumber )}>FO-{link.orderNumber}</Link>
			</Box>
			{link.transferOrderIds.map( ( id ) => (
				<Box component="span" key={id} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
					{SEPARATOR}
					<Link to={transferOrderPath( id )}>TO-{id}</Link>
				</Box>
			) )}
		</Stack>
	)
}

export type FulfillmentChainLinksProps = {
	chain: ChainLink[],
}

/**
 * Links along the fulfillment chain of a pick order.
 *
 * One fulfillment order shows its chain inline. Several collapse behind a
 * button that opens every chain, so a row in a table stays one line tall.
 */
export default function FulfillmentChainLinks( { chain }: FulfillmentChainLinksProps ) {
	const [ anchor, setAnchor ] = useState<HTMLElement | null>( null )

	if ( chain.length === 0 ) {
		return <Typography variant="body2" color="text.secondary">—</Typography>
	}
	if ( chain.length === 1 ) {
		return <ChainRow link={chain[ 0 ]} />
	}

	const open = ( event: MouseEvent<HTMLElement> ) => setAnchor( event.currentTarget )

	return (
		<>
			<Button
				size="small"
				color="inherit"
				endIcon={<ExpandMoreIcon />}
				onClick={open}
				aria-haspopup="dialog"
				aria-expanded={!!anchor}
				sx={{ textTransform: 'none', minHeight: 36, px: 1 }}
			>
				{chain.length} fulfillment orders
			</Button>
			<Popover
				open={!!anchor}
				anchorEl={anchor}
				onClose={() => setAnchor( null )}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			>
				<Stack spacing={1.25} sx={{ p: 2 }}>
					{chain.map( ( link ) => <ChainRow key={link.orderId} link={link} /> )}
				</Stack>
			</Popover>
		</>
	)
}
